import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import type { Client, ClientContact } from '@planner/types'

export function useClients() {
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const hasFetchedRef = useRef(false)

  const fetchClients = useCallback(async () => {
    setIsFetching(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('clients')
        .select('*, client_contacts(*)')
        .order('created_at', { ascending: false })

      if (fetchError) {
        console.error('[useClients] Fetch error:', fetchError)
        setError(fetchError.message)
        setLoading(false)
      } else {
        setClients(data || [])
        setLoading(false)
      }
    } catch (err: any) {
      console.error('[useClients] Exception:', err)
      setError(err.message || 'Wystąpił nieoczekiwany błąd')
      setLoading(false)
    } finally {
      setIsFetching(false)
    }
  }, [])

  useEffect(() => {
    // Fetch on mount
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true
      fetchClients()
    }
  }, [fetchClients])

  // DON'T refetch when page becomes visible - data is already in state
  // This prevents unnecessary loading after tab switch
  // useEffect(() => {
  //   if (isVisible && hasFetchedRef.current && clients.length === 0 && !isFetching) {
  //     fetchClients()
  //   }
  // }, [isVisible, clients.length, isFetching, fetchClients])

  const createClient = useCallback(async (clientData: Omit<Client, 'id' | 'created_at' | 'updated_at' | 'client_contacts'>, contacts?: Omit<ClientContact, 'id' | 'client_id' | 'created_at' | 'updated_at'>[]) => {
    try {
      // Extract contacts from clientData if present
      const { client_contacts, ...clientDataWithoutContacts } = clientData as any
      const contactsToInsert = contacts || client_contacts || []

      const { data: client, error: createError } = await supabase
        .from('clients')
        .insert([clientDataWithoutContacts])
        .select()
        .single()

      if (createError) {
        return { data: null, error: createError.message }
      }

      // Insert contacts if provided
      if (contactsToInsert.length > 0 && client) {
        const contactsWithClientId = contactsToInsert.map(contact => ({
          ...contact,
          client_id: client.id
        }))

        const { error: contactsError } = await supabase
          .from('client_contacts')
          .insert(contactsWithClientId)

        if (contactsError) {
          console.error('Error creating client contacts:', contactsError)
          // Continue anyway - client was created successfully
        }
      }

      // Refetch after create
      await fetchClients()
      return { data: client, error: null }
    } catch (err: any) {
      return { data: null, error: err.message }
    }
  }, [fetchClients])

  const updateClient = useCallback(async (id: string, clientData: Partial<Client>) => {
    try {
      const { data, error: updateError } = await supabase
        .from('clients')
        .update(clientData)
        .eq('id', id)
        .select()
        .single()

      if (updateError) {
        return { data: null, error: updateError.message }
      }

      // Refetch after update
      await fetchClients()
      return { data, error: null }
    } catch (err: any) {
      return { data: null, error: err.message }
    }
  }, [fetchClients])

  const deleteClient = useCallback(async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('clients')
        .delete()
        .eq('id', id)

      if (deleteError) {
        return { error: deleteError.message }
      }

      // Refetch after delete
      await fetchClients()
      return { error: null }
    } catch (err: any) {
      return { error: err.message }
    }
  }, [fetchClients])

  const addClientContact = useCallback(async (clientId: string, contactData: Omit<ClientContact, 'id' | 'client_id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error: insertError } = await supabase
        .from('client_contacts')
        .insert([{ ...contactData, client_id: clientId }])
        .select()
        .single()

      if (insertError) {
        return { data: null, error: insertError.message }
      }

      // Refetch after insert
      await fetchClients()
      return { data, error: null }
    } catch (err: any) {
      return { data: null, error: err.message }
    }
  }, [fetchClients])

  const updateClientContact = useCallback(async (contactId: string, contactData: Partial<ClientContact>) => {
    try {
      const { data, error: updateError } = await supabase
        .from('client_contacts')
        .update(contactData)
        .eq('id', contactId)
        .select()
        .single()

      if (updateError) {
        return { data: null, error: updateError.message }
      }

      // Refetch after update
      await fetchClients()
      return { data, error: null }
    } catch (err: any) {
      return { data: null, error: err.message }
    }
  }, [fetchClients])

  const deleteClientContact = useCallback(async (contactId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('client_contacts')
        .delete()
        .eq('id', contactId)

      if (deleteError) {
        return { error: deleteError.message }
      }

      // Refetch after delete
      await fetchClients()
      return { error: null }
    } catch (err: any) {
      return { error: err.message }
    }
  }, [fetchClients])

  // loading = true tylko gdy faktycznie fetchujemy i nie mamy danych
  const isLoading = isFetching && clients.length === 0

  return {
    clients,
    loading: isLoading,
    error,
    fetchClients,
    createClient,
    updateClient,
    deleteClient,
    addClientContact,
    updateClientContact,
    deleteClientContact,
  }
}
